import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { createStreamSession, parseSSEBlocks, resetSessionFlags, resetStreamSession } from "@/components/document-chat/utils";
import { useDocumentChatRuntime } from "@/store/documentChatRuntime";
import { ChatStatus } from "@/constants/enums";
import { DocumentChatRuntime } from "../useChatSSE/types";
import { processSSEEvents } from "../useChatSSE/eventProcessor";
import { setupStreamRequest, initializeStream, handleStreamError, cleanupStream } from "../useChatSSE/utils";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL as string;

/**
 * Streams chat responses from the backend via Server-Sent Events for a single prompt.
 *
 * Opens a streaming GET request to `/ask/stream` and aggregates incremental text
 * until completion or cancellation. Consumers trigger streaming via `send(...)`.
 *
 * @param options Optional configuration like endpoint, default collection/session, and auth token getter
 * @returns API to send prompts and observe streaming state
 */
export function useChatSSE(options?: UseChatSSEOptions) {
  const endpoint = options?.endpoint ?? `${BACKEND_URL}/ask/stream`;
  const { t } = useTranslation(["components"]);

  const update = useDocumentChatRuntime((s) => s.update);
  const appendThinking = useDocumentChatRuntime((s) => s.appendThinking);
  const addToolCall = useDocumentChatRuntime((s) => s.addToolCall);
  const finishToolCall = useDocumentChatRuntime((s) => s.finishToolCall);
  const markThinkingStart = useDocumentChatRuntime((s) => s.markThinkingStart);
  const markAnswerStart = useDocumentChatRuntime((s) => s.markAnswerStart);
  const resetRuntime = useDocumentChatRuntime((s) => s.resetRuntime);
  const appendStreamingToken = useDocumentChatRuntime((s) => s.appendStreamingToken);
  const clearStreamingContent = useDocumentChatRuntime((s) => s.clearStreamingContent);

  // Centralized runtime store interface
  const runtime: DocumentChatRuntime = useMemo(
    () => ({
      update,
      appendThinking,
      addToolCall,
      finishToolCall,
      markThinkingStart,
      markAnswerStart,
      resetRuntime,
      appendStreamingToken,
      clearStreamingContent
    }),
    [
      update,
      appendThinking,
      addToolCall,
      finishToolCall,
      markThinkingStart,
      markAnswerStart,
      resetRuntime,
      appendStreamingToken,
      clearStreamingContent
    ]
  );

  // Stream session
  const sessionRef = useRef<StreamSession>(createStreamSession());

  // Helper to get current state (reads fresh values from store)
  const getStateSnapshot = useCallback(
    () => ({
      currentNode: useDocumentChatRuntime.getState().currentNode,
      processingStagesComplete: useDocumentChatRuntime.getState().processingStagesComplete
    }),
    []
  );

  const cancel = useCallback(() => {
    const session = sessionRef.current;
    session.abortController?.abort();
    session.abortController = null;
  }, []);

  const reset = useCallback(() => {
    // Stop any active stream and clear runtime state
    try {
      cancel();
    } catch {
      /* noop */
    }
    runtime.resetRuntime();
    runtime.clearStreamingContent();
    resetStreamSession(sessionRef.current);
  }, [cancel, runtime]);

  const send = useCallback(
    async (params: SendParams) => {
      // Cancel any existing stream and reset transient state
      cancel();
      runtime.update({ error: null, streamingContent: "" });
      runtime.clearStreamingContent();

      // Reset streaming flags and processing state for new message
      resetSessionFlags(sessionRef.current);
      runtime.update({ processingStagesComplete: false });

      const controller = new AbortController();
      sessionRef.current.abortController = controller;

      try {
        runtime.update({ status: ChatStatus.CONNECTING });

        const { search } = setupStreamRequest(params, options);
        const reader = await initializeStream(
          endpoint,
          search,
          controller,
          sessionRef.current,
          runtime,
          options?.getAuthToken,
          t
        );

        // Streaming loop
        while (true) {
          const { value, done } = await reader.read();
          if (done) {
            break;
          }
          if (!value) continue;

          sessionRef.current.buffer += value;
          const { events, remainder } = parseSSEBlocks(sessionRef.current.buffer);
          sessionRef.current.buffer = remainder;

          processSSEEvents(events, sessionRef.current, runtime, cancel, getStateSnapshot, t);
        }

        // Only set to "closed" if we haven't encountered an error
        // (error events set status to "error" which should be preserved)
        if (!sessionRef.current.hasError) {
          runtime.update({ status: ChatStatus.CLOSED });
        }

        // DO NOT reset runtime immediately - let the chat thread detect stream completion
        // and trigger DB refetch first. The chat thread will clear pending state after refetch.
        // resetRuntime() is now handled by the chat thread after message sync completes.
      } catch (e: unknown) {
        handleStreamError(e, sessionRef.current, runtime, t);
      } finally {
        await cleanupStream(sessionRef.current);
      }
    },
    [cancel, runtime, endpoint, options, t, getStateSnapshot]
  );

  // Keep the hook's surface area minimal: parent reads state from the runtime store
  return { send, cancel, reset } as const;
}
